// Track shapes from OpenStreetMap, for cities whose operators publish no
// shapes (London's TfL, Hong Kong's MTR, Singapore's LTA): the real track
// between every pair of neighboring stations on every line, found by a
// shortest path over the ways of the line's OSM route relations. A city's
// build script fetches the relations (fetchOverpass), groups their ways by
// line (waysByLine) and writes trackSegments' output as geometry.json.
//
// The output is derived from OpenStreetMap and so is licensed under the ODbL:
// https://www.openstreetmap.org/copyright

import { dist } from './core.ts';
import type { LatLngTuple, LatLon, LineId, Network, StationId } from './types.ts';

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;

export type OsmTags = Readonly<Partial<Record<string, string>>>;

interface OsmMember {
  readonly type: string;
  readonly ref: number;
  readonly role: string;
}

export interface OsmRelation {
  readonly type: 'relation';
  readonly id: number;
  readonly tags: OsmTags;
  readonly members: readonly OsmMember[];
}

// A way from `out geom`: node ids with their coordinates alongside.
export interface OsmWay {
  readonly type: 'way';
  readonly id: number;
  readonly nodes: readonly number[];
  readonly geometry: readonly LatLon[];
}

export interface OsmNode {
  readonly type: 'node';
  readonly id: number;
  readonly lat: number;
  readonly lon: number;
  readonly tags: OsmTags;
}

const isTags = (v: unknown): v is OsmTags => isRecord(v) && Object.values(v).every((x) => typeof x === 'string');

const isOsmMember = (v: unknown): v is OsmMember =>
  isRecord(v) && typeof v['type'] === 'string' && typeof v['ref'] === 'number' && typeof v['role'] === 'string';

export const isOsmRelation = (v: unknown): v is OsmRelation =>
  isRecord(v) &&
  v['type'] === 'relation' &&
  typeof v['id'] === 'number' &&
  isTags(v['tags']) &&
  Array.isArray(v['members']) &&
  v['members'].every(isOsmMember);

const isLatLon = (v: unknown): v is LatLon =>
  isRecord(v) && typeof v['lat'] === 'number' && typeof v['lon'] === 'number';

export const isOsmWay = (v: unknown): v is OsmWay =>
  isRecord(v) &&
  v['type'] === 'way' &&
  typeof v['id'] === 'number' &&
  Array.isArray(v['nodes']) &&
  v['nodes'].every((n) => typeof n === 'number') &&
  Array.isArray(v['geometry']) &&
  v['geometry'].every(isLatLon);

export const isOsmNode = (v: unknown): v is OsmNode =>
  isRecord(v) &&
  v['type'] === 'node' &&
  typeof v['id'] === 'number' &&
  typeof v['lat'] === 'number' &&
  typeof v['lon'] === 'number' &&
  (v['tags'] === undefined || isTags(v['tags']));

// An Overpass reply's elements. An error remark (an HTTP-success reply
// carrying e.g. "runtime error: Query timed out") is rejected, not
// published as empty geometry.
export function osmElements(reply: unknown): readonly unknown[] {
  if (!isRecord(reply) || !Array.isArray(reply['elements']))
    throw new TypeError('Overpass reply has no elements array');
  const remark = reply['remark'];
  if (typeof remark === 'string' && remark !== '' && /error/iu.test(remark))
    throw new TypeError(`Overpass error: ${remark}`);
  const elements: readonly unknown[] = reply['elements'];
  return elements;
}

export const OVERPASS = 'https://overpass-api.de/api/interpreter';

// POST a query to Overpass. The main instance often answers 504 under load;
// OVERPASS_URL=https://overpass.private.coffee/api/interpreter is a mirror.
export async function fetchOverpass(
  query: string,
  url: string = OVERPASS,
  userAgent = 'transitof.com build',
): Promise<unknown> {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded', 'user-agent': userAgent },
    body: 'data=' + encodeURIComponent(query),
  });
  if (!res.ok)
    throw new Error(`Overpass HTTP ${res.status}. Try OVERPASS_URL=https://overpass.private.coffee/api/interpreter`);
  return res.json();
}

// Each line's track: the untagged-role (track, not platform or stop) ways of
// the route relations `lineOf` assigns to it.
export function waysByLine(
  elements: readonly unknown[],
  lineOf: (relation: OsmRelation) => LineId | null,
): Map<LineId, OsmWay[]> {
  const wayById = new Map(elements.filter((e) => isOsmWay(e)).map((w) => [w.id, w]));
  const out = new Map<LineId, OsmWay[]>();
  for (const r of elements.filter((e) => isOsmRelation(e))) {
    const lineId = lineOf(r);
    if (lineId === null) continue;
    let list = out.get(lineId);
    if (!list) out.set(lineId, (list = []));
    for (const m of r.members) {
      const w = m.type === 'way' && m.role === '' ? wayById.get(m.ref) : undefined;
      if (w && !list.includes(w)) list.push(w);
    }
  }
  return out;
}

// ---- geometry helpers (meters on a local plane)

const RAD = Math.PI / 180;
const d2 = (a: LatLngTuple, b: LatLngTuple): number => dist({ lat: a[0], lon: a[1] }, { lat: b[0], lon: b[1] });

// Douglas-Peucker, tolerance in meters.
export function simplify(pts: readonly LatLngTuple[], tol: number): readonly LatLngTuple[] {
  const p0 = pts[0];
  if (pts.length < 3 || !p0) return pts;
  const lat0 = p0[0] * RAD;
  const xy = pts.map(([la, lo]) => [lo * RAD * Math.cos(lat0) * 6371000, la * RAD * 6371000] as const);
  const keep = new Uint8Array(pts.length);
  keep[0] = keep[pts.length - 1] = 1;
  const stack: [number, number][] = [[0, pts.length - 1]];
  for (let top = stack.pop(); top; top = stack.pop()) {
    const [i, j] = top;
    const pa = xy[i];
    const pb = xy[j];
    if (!pa || !pb) continue;
    const [ax, ay] = pa;
    const [bx, by] = pb;
    const dx = bx - ax;
    const dy = by - ay;
    const L2 = dx * dx + dy * dy || 1;
    let best = -1;
    let bd = tol;
    for (let k = i + 1; k < j; k++) {
      const q = xy[k];
      if (!q) continue;
      const t = Math.max(0, Math.min(1, ((q[0] - ax) * dx + (q[1] - ay) * dy) / L2));
      const d = Math.hypot(q[0] - (ax + t * dx), q[1] - (ay + t * dy));
      if (d > bd) {
        bd = d;
        best = k;
      }
    }
    if (best > 0) {
      keep[best] = 1;
      stack.push([i, best], [best, j]);
    }
  }
  return pts.filter((_, i) => keep[i] === 1);
}

const enc = (n: number): string => {
  let v = n < 0 ? ~(n << 1) : n << 1;
  let s = '';
  while (v >= 0x20) {
    s += String.fromCodePoint((0x20 | (v & 0x1f)) + 63);
    v >>= 5;
  }
  return s + String.fromCodePoint(v + 63);
};

// Google polyline encoding, precision 5 (about 1 m): decodePolyline's input.
export function encodePolyline(pts: readonly LatLngTuple[]): string {
  let out = '';
  let plat = 0;
  let plon = 0;
  for (const [la, lo] of pts) {
    const a = Math.round(la * 1e5);
    const b = Math.round(lo * 1e5);
    out += enc(a - plat) + enc(b - plon);
    plat = a;
    plon = b;
  }
  return out;
}

type HeapEntry = readonly [key: number, value: number];

// Tiny binary heap for Dijkstra.
class Heap {
  private readonly a: HeapEntry[] = [];
  private key(i: number): number {
    return this.a[i]?.[0] ?? Infinity;
  }
  private swap(i: number, j: number): void {
    const x = this.a[i];
    const y = this.a[j];
    if (!x || !y) return;
    this.a[i] = y;
    this.a[j] = x;
  }
  push(k: number, v: number): void {
    const a = this.a;
    a.push([k, v]);
    let i = a.length - 1;
    while (i > 0) {
      const p = (i - 1) >> 1;
      if (this.key(p) <= this.key(i)) break;
      this.swap(p, i);
      i = p;
    }
  }
  pop(): HeapEntry | undefined {
    const a = this.a;
    const top = a[0];
    const last = a.pop();
    if (a.length > 0 && last) {
      a[0] = last;
      let i = 0;
      for (;;) {
        const l = 2 * i + 1;
        const r = l + 1;
        let m = i;
        if (l < a.length && this.key(l) < this.key(m)) m = l;
        if (r < a.length && this.key(r) < this.key(m)) m = r;
        if (m === i) break;
        this.swap(m, i);
        i = m;
      }
    }
    return top;
  }
}

type Near = readonly (readonly [id: number, d: number])[];

interface Graph {
  readonly coord: ReadonlyMap<number, LatLngTuple>;
  readonly adj: ReadonlyMap<number, readonly (readonly [to: number, w: number])[]>;
  readonly near: (p: LatLngTuple, radius: number) => Near;
}

const cell = (la: number, lo: number): string => `${Math.floor(la / 0.004)},${Math.floor(lo / 0.006)}`;

function buildGraph(ways: readonly OsmWay[]): Graph {
  const coord = new Map<number, LatLngTuple>();
  const adj = new Map<number, [number, number][]>();
  const link = (a: number, b: number, w: number): void => {
    const list = adj.get(a);
    if (list) list.push([b, w]);
    else adj.set(a, [[b, w]]);
  };
  for (const w of ways) {
    for (const [i, node] of w.nodes.entries()) {
      const g = w.geometry[i];
      if (!g) throw new TypeError(`way ${w.id} has no geometry for node ${node}`);
      coord.set(node, [g.lat, g.lon]);
      const a = w.nodes[i - 1];
      const pa = a === undefined ? undefined : coord.get(a);
      const pb = coord.get(node);
      if (i > 0 && a !== undefined && pa && pb) {
        const d = d2(pa, pb);
        link(a, node, d);
        link(node, a, d);
      }
    }
  }
  // Grid index for snapping stations to nearby track.
  const grid = new Map<string, number[]>();
  for (const [id, [la, lo]] of coord) {
    const k = cell(la, lo);
    const list = grid.get(k);
    if (list) list.push(id);
    else grid.set(k, [id]);
  }
  const near = (p: LatLngTuple, radius: number): Near => {
    const out: [number, number][] = [];
    const ci = Math.floor(p[0] / 0.004);
    const cj = Math.floor(p[1] / 0.006);
    const span = Math.ceil(radius / 400);
    for (let i = ci - span; i <= ci + span; i++)
      for (let j = cj - span; j <= cj + span; j++)
        for (const id of grid.get(`${i},${j}`) ?? []) {
          const c = coord.get(id);
          if (!c) continue;
          const d = d2(p, c);
          if (d <= radius) out.push([id, d]);
        }
    return out;
  };
  return { coord, adj, near };
}

interface TrackPath {
  readonly pts: readonly LatLngTuple[];
  readonly len: number;
  readonly D: number;
}

// Shortest track path from any node near A to any node near B. Snapping
// distance counts extra so the path prefers track that actually reaches the
// stations over a closer-looking siding.
function trackPath(
  g: Graph,
  A: LatLngTuple,
  B: LatLngTuple,
  stray: number,
  allowed?: (point: LatLngTuple) => boolean,
): TrackPath | null {
  const D = d2(A, B);
  const limit = Math.max(3 * D, D + 3000, stray * D);
  const accepts = (id: number): boolean => {
    const point = g.coord.get(id);
    return point !== undefined && (allowed === undefined || allowed(point));
  };
  for (const radius of [350, 700]) {
    const src = g.near(A, radius).filter(([id]) => accepts(id));
    const dst = new Map(
      g
        .near(B, radius)
        .filter(([id]) => accepts(id))
        .map(([id, d]) => [id, d * 1.5]),
    );
    if (src.length === 0 || dst.size === 0) continue;
    const best = new Map<number, number>();
    const prev = new Map<number, number>();
    const h = new Heap();
    for (const [id, d] of src) {
      best.set(id, d * 1.5);
      h.push(d * 1.5, id);
    }
    let found = Infinity;
    let end: number | null = null;
    for (let top = h.pop(); top; top = h.pop()) {
      const [c, u] = top;
      // u always has a distance once pushed.
      if (c > (best.get(u) ?? Infinity)) continue;
      if (c >= found || c > limit) break;
      const du = dst.get(u);
      if (du !== undefined && c + du < found) {
        found = c + du;
        end = u;
      }
      for (const [v, w] of g.adj.get(u) ?? []) {
        if (allowed !== undefined && !accepts(v)) continue;
        const nc = c + w;
        if (nc < (best.get(v) ?? Infinity)) {
          best.set(v, nc);
          prev.set(v, u);
          h.push(nc, v);
        }
      }
    }
    if (end === null) continue;
    const ids = [end];
    for (let p = prev.get(end); p !== undefined; p = prev.get(p)) ids.push(p);
    ids.reverse();
    const pts: LatLngTuple[] = [A];
    for (const id of ids) {
      const c = g.coord.get(id);
      if (!c) throw new TypeError(`node ${id} has no coordinates`);
      pts.push(c);
    }
    pts.push(B);
    let len = 0;
    for (let i = 1; i < pts.length; i++) {
      const p = pts[i - 1];
      const q = pts[i];
      if (p && q) len += d2(p, q);
    }
    return { pts, len, D };
  }
  return null;
}

export interface TrackSegments {
  // geometry.json's segments: line id -> 'a|b' (a < b) -> encoded polyline.
  readonly segments: Readonly<Record<LineId, Readonly<Record<string, string>>>>;
  // Per line: segments on real track, segments, OSM ways.
  readonly lines: readonly (readonly [lineId: LineId, ok: number, total: number, ways: number])[];
  // Stretches left straight, and why.
  readonly misses: readonly string[];
  // Accepted detours, biggest first (track length over straight distance).
  readonly detours: readonly (readonly [ratio: number, text: string])[];
}

// How far a run along the track may stray before its stretch is left
// straight: `ratio` times the straight line, or `plus` m more, whichever is
// longer; and whether a run that strays (past 1.4 times) may pass another of
// the line's stations. Defaults: 2.2, 2200 m, no (London's). A timetable's
// regional trains skip stations and take the long way round into a
// terminus (Munich's airport to Freising, 16.9 km of track for 5.6 km).
export interface TrackLimits {
  readonly ratio?: number;
  readonly plus?: number;
  readonly passes?: boolean;
  // Per-line restrictions on usable railway nodes, such as a train's
  // direction at a station. Lines still share the same underlying graph.
  readonly allowed?: (line: LineId, point: LatLngTuple) => boolean;
}

// The track between every pair of neighboring stations on every line in
// `ways` that the network has.
export function trackSegments(
  net: Network,
  ways: ReadonlyMap<LineId, readonly OsmWay[]>,
  limits: TrackLimits = {},
): TrackSegments {
  const { ratio = 2.2, plus = 2200, passes = false, allowed } = limits;
  const station = (id: StationId): { readonly name: string; readonly P: LatLngTuple } => {
    const s = net.stations.get(id);
    if (!s) throw new TypeError(`station ${id} is not in the network`);
    return { name: s.name, P: [s.lat, s.lon] };
  };
  const segments: Record<LineId, Record<string, string>> = {};
  const lines: (readonly [LineId, number, number, number])[] = [];
  const misses: string[] = [];
  const detours: (readonly [number, string])[] = [];
  // Lines given the same ways (a country's one network of track, France's)
  // share one graph, and a hop two of them share is searched once. Ways only
  // one line uses get their graph built, used and dropped with that line, so
  // a build over many networks of track holds one graph at a time instead of
  // all of them until the build ends.
  const each = new Map<readonly OsmWay[], number>();
  for (const lineWays of ways.values()) each.set(lineWays, (each.get(lineWays) ?? 0) + 1);
  const graphs = new Map<readonly OsmWay[], { readonly g: Graph; readonly paths: Map<string, TrackPath | null> }>();
  for (const [lineId, lineWays] of ways) {
    const L = net.lines[lineId];
    if (!L) continue;
    const cache = (each.get(lineWays) ?? 0) > 1 ? graphs : null;
    let entry = cache?.get(lineWays);
    if (!entry) {
      entry = { g: buildGraph(lineWays), paths: new Map() };
      cache?.set(lineWays, entry);
    }
    const { g, paths } = entry;
    const lineSegments: Record<string, string> = {};
    segments[lineId] = lineSegments;
    let ok = 0;
    let total = 0;
    for (const [a, nb] of L.adj) {
      for (const b of nb) {
        if (a > b) continue;
        total++;
        const sa = station(a);
        const sb = station(b);
        const A = sa.P;
        const B = sb.P;
        // A restricted path belongs to its line; an unrestricted path can
        // be reused by every line sharing the graph and station pair.
        const key = allowed === undefined ? `${a}|${b}` : `${lineId}\n${a}|${b}`;
        let r = paths.get(key);
        if (r === undefined)
          paths.set(
            key,
            (r = trackPath(g, A, B, ratio, allowed === undefined ? undefined : (point) => allowed(lineId, point))),
          );
        // Reject implausible detours; that stretch stays a straight line. The
        // allowance covers real ones like the Heathrow Terminal 4 loop.
        if (!r || r.len > Math.max(ratio * r.D, r.D + plus)) {
          misses.push(
            `${lineId}: ${sa.name} - ${sb.name}${r ? ` (track ${Math.round(r.len)} m vs ${Math.round(r.D)} m straight)` : ' (no track found)'}`,
          );
          continue;
        }
        // A path that detours *and* runs through a third station on the same
        // line has doubled back (OSM missing a loop, as at Heathrow T4).
        // Passing a station on a direct path is normal (Metropolitan fast
        // trains through Willesden Green), so only the combination is
        // rejected. Stations close to either end are exempt: TfL lists some
        // complexes as two stops.
        const via =
          !passes && r.len > 1.4 * r.D
            ? [...L.ids].find((id) => {
                if (id === a || id === b) return false;
                const P3 = station(id).P;
                if (d2(P3, A) < 400 || d2(P3, B) < 400) return false;
                return r.pts.some((q) => d2(q, P3) < 250);
              })
            : undefined;
        if (via !== undefined && via !== '') {
          misses.push(`${lineId}: ${sa.name} - ${sb.name} (track passes ${station(via).name})`);
          continue;
        }
        detours.push([
          r.len / r.D,
          `${lineId}: ${sa.name} - ${sb.name} (${Math.round(r.len)} m track, ${Math.round(r.D)} m straight)`,
        ]);
        const pts = simplify(r.pts, 4);
        // Keep accepted two-point paths: absence means no matched geometry.
        if (pts.length >= 2) lineSegments[`${a}|${b}`] = encodePolyline(pts);
        ok++;
      }
    }
    lines.push([lineId, ok, total, lineWays.length]);
  }
  return { segments, lines, misses, detours: detours.toSorted((x, y) => y[0] - x[0]) };
}

// trackSegments' outcome, for a build script to print.
export function trackReport(t: TrackSegments): string {
  const ok = t.lines.reduce((n, l) => n + l[1], 0);
  const total = t.lines.reduce((n, l) => n + l[2], 0);
  return [
    ...t.lines.map(
      ([id, k, n, w]) => `${id.padEnd(17)} ${String(k).padStart(3)}/${n} segments on real track (${w} OSM ways)`,
    ),
    `\n${ok}/${total} segments matched.`,
    'Biggest accepted detours (check these look right):\n  ' +
      t.detours
        .slice(0, 8)
        .map((d) => d[0].toFixed(2) + 'x ' + d[1])
        .join('\n  '),
    ...(t.misses.length > 0 ? ['Straight-line fallbacks:\n  ' + t.misses.join('\n  ')] : []),
  ].join('\n');
}

// geometry.json.
export function geometryFile(segments: TrackSegments['segments'], generated: string): string {
  return JSON.stringify({
    source: 'OpenStreetMap route relations via Overpass',
    license: 'ODbL-1.0, © OpenStreetMap contributors, https://www.openstreetmap.org/copyright',
    generated,
    format: 'segments[lineId]["stationA|stationB"] = Google-encoded polyline from A to B, precision 5',
    segments,
  });
}

// ---- stations by name

// A station from `nwr[railway=station]; out center tags;`: a node's own
// position, a way's or relation's center.
export interface OsmPlace extends LatLon {
  readonly tags: OsmTags;
}

export function osmPlaces(elements: readonly unknown[]): OsmPlace[] {
  const out: OsmPlace[] = [];
  for (const e of elements) {
    if (!isRecord(e) || !isTags(e['tags'])) continue;
    const at = isLatLon(e) ? e : isLatLon(e['center']) ? e['center'] : null;
    if (at) out.push({ lat: at.lat, lon: at.lon, tags: e['tags'] });
  }
  return out;
}

// For comparing names: lowercase letters and digits of any script, without
// the word "station".
export const placeName = (s: string): string =>
  s
    .toLowerCase()
    .replaceAll(/\bstation\b/gu, '')
    .replaceAll(/[^\p{L}\p{N}]/gu, '');

// Where the stations named any of `names` (in name, name:en or name:zh)
// that `accept` takes are: their mean position, or null. A station mapped
// twice (a node and its building, two platforms' nodes) counts once each.
export function placeNamed(
  places: readonly OsmPlace[],
  names: readonly string[],
  accept: (tags: OsmTags) => boolean = () => true,
): LatLon | null {
  const wanted = new Set(names.map((n) => placeName(n)));
  const hits = places.filter(
    (p) =>
      accept(p.tags) &&
      ['name', 'name:en', 'name:zh', 'name:zh-Hant'].some((k) => {
        const v = p.tags[k];
        return v !== undefined && wanted.has(placeName(v));
      }),
  );
  if (hits.length === 0) return null;
  return {
    lat: hits.reduce((s, p) => s + p.lat, 0) / hits.length,
    lon: hits.reduce((s, p) => s + p.lon, 0) / hits.length,
  };
}

// A route relation's stops in order: its members with a stop role (stop,
// stop_entry_only, stop_exit_only), as the nodes they are.
export function relationStops(relation: OsmRelation, elements: readonly unknown[]): OsmNode[] {
  const nodes = new Map(elements.filter((e) => isOsmNode(e)).map((n) => [n.id, n]));
  return relation.members.flatMap((m) => {
    const n = m.type === 'node' && m.role.startsWith('stop') ? nodes.get(m.ref) : undefined;
    return n ? [n] : [];
  });
}
